import { PulseStorage, TrackerConfigurations } from '@pulse/common';
import { BehaviorSubject } from 'rxjs';

/**
 * Help enable debugging for a specific service/module.
 */
export class DebugService {
    readonly items: BehaviorSubject<Set<string>>;
    private debugItems?: Set<string>;
    private readonly KEY_DEBUG: string = 'debug';

    constructor(
        private storage: PulseStorage,
        config: TrackerConfigurations,
    ) {
        storage.change(this.KEY_DEBUG).subscribe((change) => this.getDebugKeys(change.value));
        const debugItems = this.getDebugKeys();
        this.items = new BehaviorSubject(debugItems);

        if (Array.isArray(config.debug) && config.debug.length) {
            this.enable(...config.debug);
        }

        if (config.debugClear) {
            this.disableAll();
        }
    }

    /**
     * Check if debug for a specific module or service is enabled.
     */
    enabled(module: string): boolean {
        const debug = this.getDebugKeys();

        return debug.has(module) || debug.has('all');
    }

    /**
     * Enable debug for specific module/service.
     */
    enable(...modules: string[]): void {
        const debug = this.getDebugKeys();
        let changed = false;

        for (const module of modules) {
            changed = changed || !debug.has(module);
            debug.add(module);
        }

        if (changed) {
            this.set(Array.from(debug));
        }
    }

    disable(...modules: string[]): void {
        const debug = this.getDebugKeys();
        let changed = false;

        for (const module of modules) {
            changed = changed || debug.has(module);
            debug.delete(module);
        }

        if (changed) {
            this.set(Array.from(debug));
        }
    }

    disableAll(): void {
        const debug = this.getDebugKeys();
        debug.clear();
        this.set([]);
    }

    set(debugKeys: string[]): void {
        this.storage.set(this.KEY_DEBUG, JSON.stringify(debugKeys));
        this.debugItems = new Set(debugKeys);
        this.items.next(this.debugItems);
    }

    private getDebugKeys(override?: string | null): Set<string> {
        if (!this.debugItems || override) {
            let debugKeys: string[];

            try {
                debugKeys = JSON.parse(override || this.storage.get(this.KEY_DEBUG) || '');

                if (!Array.isArray(debugKeys)) {
                    throw new Error('Failed to parse debug mode!');
                }
            } catch {
                debugKeys = [];
            }

            this.debugItems = new Set(debugKeys);
        }

        return this.debugItems;
    }
}
